import * as Api from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getDisplayName } from '../../../models/NameUtils';
import { AppState } from '../../models/AppState';
import { IRealtimeTranscriptionUtteranceEvent } from '../../viewModels/phonecall';
import { IWebsocketSubscription } from '../../viewModels/websockets';

export const TwilioContext = createContext(null);

export interface IKeywordSuggestion {
	title: string;
	message: string;
	score: number;
}

export interface ISalesAiSentiment {
	score: number;
	color: StyleDeclarationValue;
	keyword: string;
}

export interface ISalesAiSentimentEvent {
	id: string;
	score: number;
	color: StyleDeclarationValue;
	keyword: string;
}

// Stability threshold: >= 0.5 is stable (confirmed text)
const STABILITY_THRESHOLD = 0.5;

// Track partial utterances per speaker track
interface PartialUtteranceState {
	stableText: string;
	highestSeqSeen: number;
}

export const TwilioContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { websockets } = AppState;
	const [suggestions, setSuggestions] = useState<IKeywordSuggestion[]>([]);
	const [completeTopicIdentifiers, setCompleteTopicIdentifiers] = useState<string[]>([]);
	const [incompleteTopicIdentifiers, setIncompleteTopicIdentifiers] = useState<string[]>([]);
	const [realtimeTranscriptionUtterances, setRealtimeTranscriptionUtterances] = useState<string[]>([]);

	// Track partial utterances by track for proper handling
	const partialsByTrackRef = useRef<Map<string, PartialUtteranceState>>(new Map());
	const highestSeqByTrackRef = useRef<Map<string, number>>(new Map());

	const realtimeTranscriptionUtteranceHintHandler = useCallback(
		(events: Api.IRemoteEvent<IRealtimeTranscriptionUtteranceEvent>[]) => {
			for (const event of events) {
				const utterance = event.value;
				const seqId = parseInt(utterance.sequenceId, 10);
				const stability = parseFloat(utterance.stability || '1');
				const track = utterance.speaker; // 'inbound_track' or 'outbound_track'
				const speaker = track === 'inbound_track' ? 'Lead' : 'SDR';

				// Out-of-order rejection
				const highestSeen = highestSeqByTrackRef.current.get(track) || 0;
				if (seqId < highestSeen) {
					// Skip out-of-order events
					continue;
				}
				highestSeqByTrackRef.current.set(track, seqId);

				// Get or create partial state for this track
				let partial = partialsByTrackRef.current.get(track);
				if (!partial) {
					partial = { stableText: '', highestSeqSeen: seqId };
					partialsByTrackRef.current.set(track, partial);
				}

				partial.highestSeqSeen = seqId;

				// Update stable text if this event has high stability
				if (stability >= STABILITY_THRESHOLD) {
					partial.stableText = utterance.transcription;
				}

				// Only add to transcript when utterance is final
				if (utterance.final) {
					const finalText = partial.stableText || utterance.transcription;
					if (finalText.trim()) {
						setRealtimeTranscriptionUtterances(prev => [...prev, `${speaker}: ${finalText.trim()}`]);
					}

					// Clear partial for this track - ready for next utterance
					partialsByTrackRef.current.delete(track);
				}
			}
		},
		[]
	);

	const startNewRealtimeTranscriptionUtterance = useCallback((phoneCall: Api.IPhoneCall, vertical: string) => {
		const sdrName = getDisplayName(phoneCall.creator);

		// Clear tracking state for new call
		partialsByTrackRef.current = new Map();
		highestSeqByTrackRef.current = new Map();

		setRealtimeTranscriptionUtterances([
			`Call started between ${sdrName} and a lead in the ${vertical ?? 'Unknown'} vertical.`,
		]);
	}, []);

	const clearRealtimeTranscriptionUtterances = useCallback(() => {
		partialsByTrackRef.current = new Map();
		highestSeqByTrackRef.current = new Map();
		setRealtimeTranscriptionUtterances([]);
	}, []);

	useEffect(() => {
		const realtimeTranscriptionUtteranceHint: IWebsocketSubscription = {
			callback: realtimeTranscriptionUtteranceHintHandler,
			events: ['RealtimeTranscriptionUtteranceEvent'],
			name: 'realtimeTranscriptionUtteranceEvent',
		};

		websockets.subscribe(realtimeTranscriptionUtteranceHint);

		return () => {
			websockets.unsubscribe('realtimeTranscriptionUtteranceEvent');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const context = {
		suggestions,
		setSuggestions,
		completeTopicIdentifiers,
		setCompleteTopicIdentifiers,
		incompleteTopicIdentifiers,
		setIncompleteTopicIdentifiers,
		realtimeTranscriptionUtterances,
		setRealtimeTranscriptionUtterances,
		clearRealtimeTranscriptionUtterances,
		startNewRealtimeTranscriptionUtterance,
	};
	return <TwilioContext.Provider value={context}>{children}</TwilioContext.Provider>;
};

export const useTwilio = () => {
	const context = useContext(TwilioContext);

	if (!context) {
		throw new Error('Cannot use Twilio without context');
	}

	return context;
};
